import { Injectable, NgZone, OnDestroy } from '@angular/core';
import { fromEvent, interval, merge, Subscription } from 'rxjs';
import { throttleTime } from 'rxjs/operators';
import { AuthService } from 'src/app/auth/auth.service';
import { LocalStorageService } from 'src/app/services/LocalStorage.service';
import { LocalStorageKeys } from '../enums/LocalStorageKeys';

@Injectable({
  providedIn: 'root',
})
export class SessionActivityService implements OnDestroy {
  private readonly activityKey = LocalStorageKeys.LastActivityAt;
  private readonly idleTimeoutMs = 12 * 60 * 60 * 1000;
  private readonly keepAliveIntervalMs = 10 * 60 * 1000;
  private readonly idleCheckIntervalMs = 60 * 1000;
  private readonly activityThrottleMs = 30 * 1000;

  private started = false;
  private lastActivity = Date.now();
  private readonly subs = new Subscription();

  constructor(
    private authService: AuthService,
    private ngZone: NgZone,
    private localStorageService: LocalStorageService
  ) {}

  start(): void {
    if (this.started) return;
    this.started = true;

    const stored = this.readStoredActivity();
    if (stored) {
      this.lastActivity = stored;
    }

    this.ngZone.runOutsideAngular(() => {
      const activity$ = merge(
        fromEvent(window, 'mousemove'),
        fromEvent(window, 'mousedown'),
        fromEvent(window, 'keydown'),
        fromEvent(window, 'touchstart'),
        fromEvent(window, 'scroll'),
        fromEvent(window, 'visibilitychange')
      ).pipe(throttleTime(this.activityThrottleMs, undefined, { leading: true, trailing: true }));

      this.subs.add(activity$.subscribe((event) => this.recordActivity(event)));

      this.subs.add(
        interval(this.keepAliveIntervalMs).subscribe(() => {
          if (!this.authService.isLoggedIn()) return;
          if (this.authService.isInteractionInProgress()) return;
          if (this.isIdle()) return;
          void this.authService.getAccessToken();
        })
      );

      this.subs.add(
        interval(this.idleCheckIntervalMs).subscribe(() => {
          if (!this.authService.isLoggedIn()) return;
          if (this.authService.isInteractionInProgress()) return;
          if (this.isIdle()) {
            this.authService.logout();
          }
        })
      );

      this.subs.add(
        fromEvent<StorageEvent>(window, 'storage').subscribe((event) => {
          if (event.key !== this.activityKey || !event.newValue) return;
          const value = Number(event.newValue);
          if (!Number.isNaN(value)) {
            this.lastActivity = value;
          }
        })
      );
    });
  }

  touch(): void {
    this.recordActivity();
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
  }

  private recordActivity(event?: Event): void {
    if (event?.type === 'visibilitychange' && document.hidden) {
      return;
    }
    const now = Date.now();
    this.lastActivity = now;
    this.localStorageService.set(this.activityKey, now);
  }

  private readStoredActivity(): number | null {
    const value = this.localStorageService.get<number>(this.activityKey);
    return typeof value === 'number' && !Number.isNaN(value) ? value : null;
  }

  private isIdle(): boolean {
    return Date.now() - this.lastActivity > this.idleTimeoutMs;
  }
}
